import { SelectRenderer } from '@ariakit/react-core/select/select-renderer'
import classNames from 'classnames'
import * as React from 'react'
import { useInRouterContext } from 'react-router'

import { MenuGroup } from '../MenuGroup'
import { ButtonRoundingType } from '../button'
import { SelectOption } from './SelectOption'
import { InternalSelectItem } from './types'
import { ListItemOrGroup, filterSelectOptions, getSelectItemProps, groupSelectItems, isSelectItem } from './utils'

interface SelectItemsVirtualizedProps {
  items: Array<ListItemOrGroup<unknown>>
  hasGroups: boolean
  itemRenderer: (item: InternalSelectItem<unknown>) => React.ReactNode
}

function SelectItemsVirtualized(props: SelectItemsVirtualizedProps) {
  return (
    <SelectRenderer
      id="select-popover-list"
      items={props.items}
      itemSize={props.hasGroups ? undefined : 28}
      gap={props.hasGroups ? undefined : 1}
      overscan={props.hasGroups ? 1 : 8}
    >
      {(groupOrItem) => {
        if (isSelectItem(groupOrItem)) {
          return props.itemRenderer(groupOrItem)
        }

        return (
          <SelectRenderer
            {...groupOrItem}
            key={groupOrItem.id}
            gap={1}
            overscan={8}
            // Add space for group label
            paddingStart={groupOrItem.label ? 20 : 4}
            // Add small padding between groups
            paddingEnd={4}
            // Button size
            itemSize={28}
            render={(rendererProps) => (
              <MenuGroup {...rendererProps} virtualized label={groupOrItem.label}>
                {rendererProps.children}
              </MenuGroup>
            )}
          >
            {props.itemRenderer}
          </SelectRenderer>
        )
      }}
    </SelectRenderer>
  )
}

interface SelectItemsProps<ItemType> {
  searchValue: string
  items?: ItemType[]
  rounding?: ButtonRoundingType
  parentTestId?: string
  virtualized?: boolean
}

export function SelectItems<ValueType, ItemType extends InternalSelectItem<ValueType>>({
  items = [],
  rounding,
  searchValue,
  parentTestId,
  virtualized,
}: SelectItemsProps<ItemType>) {
  const isInRouterContext = useInRouterContext()

  /**
   * Group the items into their actual group for rendering
   */
  const groupedItems = React.useMemo(() => groupSelectItems(items), [items])

  const filteredItems = React.useMemo(() => filterSelectOptions(groupedItems, searchValue), [groupedItems, searchValue])

  const getItem = React.useCallback(
    (itemProps: InternalSelectItem<unknown>) => {
      const { icon, ...providedProps } = itemProps
      const testId = isSelectItem(itemProps.value) ? itemProps.value.value : itemProps.value

      return (
        <SelectOption
          {...providedProps}
          key={itemProps.id}
          // Make sure the rounding is off when trigger rounding is off
          rounding={rounding === 'none' ? 'none' : undefined}
          leftContent={icon}
          // Handle link options
          {...getSelectItemProps(providedProps, isInRouterContext)}
          // Add test ids
          data-testid={parentTestId ? `${parentTestId}-${testId}` : `select-option-${testId}`}
          className="w-full"
          contentClass={classNames({ truncate: virtualized })}
        >
          {providedProps.label}
        </SelectOption>
      )
    },
    [isInRouterContext, parentTestId, rounding, virtualized]
  )

  if (!filteredItems.length) {
    return <div className="py-1 text-center text-2xs">Nothing to select</div>
  }

  if (virtualized) {
    const hasGroups = isSelectItem(filteredItems?.[0]) === false
    return <SelectItemsVirtualized hasGroups={hasGroups} itemRenderer={getItem} items={filteredItems} />
  }

  return (
    <>
      {filteredItems.map((item, index) => {
        if (isSelectItem(item)) return getItem(item)

        return (
          <MenuGroup key={index} label={item.label} className="mt-1 space-y-px first:mt-0 first:pt-0">
            {item.items.map(getItem)}
          </MenuGroup>
        )
      })}
    </>
  )
}
